/** Discrete follower/subscriber range steps used by CFV4 filter drawer sliders. */
export const FOLLOWER_RANGE_STEPS = [
  0,
  1000,
  3000,
  10000,
  40000,
  100000,
  500000,
  10000000,
] as const;

export const FOLLOWER_RANGE_STEP_LABELS = [
  '0',
  '1K',
  '3K',
  '10K',
  '40K',
  '100K',
  '500K',
  '10M',
] as const;

export type FollowerRangeValue = { min: number; max: number };

export const FOLLOWER_RANGE_DEFAULT: FollowerRangeValue = {
  min: FOLLOWER_RANGE_STEPS[0],
  max: FOLLOWER_RANGE_STEPS[FOLLOWER_RANGE_STEPS.length - 1],
};

export const FOLLOWER_RANGE_MAX_INDEX = FOLLOWER_RANGE_STEPS.length - 1;

export function followerStepIndexToValue(index: number): number {
  const clamped = Math.max(0, Math.min(Math.round(index), FOLLOWER_RANGE_MAX_INDEX));
  return FOLLOWER_RANGE_STEPS[clamped];
}

/** Snap an absolute follower count to the nearest discrete step index. */
export function followerValueToStepIndex(value: number): number {
  if (!Number.isFinite(value) || value <= FOLLOWER_RANGE_STEPS[0]) {
    return 0;
  }
  if (value >= FOLLOWER_RANGE_STEPS[FOLLOWER_RANGE_MAX_INDEX]) {
    return FOLLOWER_RANGE_MAX_INDEX;
  }

  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < FOLLOWER_RANGE_STEPS.length; i++) {
    const distance = Math.abs(FOLLOWER_RANGE_STEPS[i] - value);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = i;
    }
  }
  return nearestIndex;
}

export function isDefaultFollowerRange(
  range: FollowerRangeValue | null | undefined,
): boolean {
  if (!range) {
    return true;
  }
  return range.min === FOLLOWER_RANGE_DEFAULT.min && range.max === FOLLOWER_RANGE_DEFAULT.max;
}

export function formatFollowerStepLabel(value: number): string {
  const index = FOLLOWER_RANGE_STEPS.indexOf(value as (typeof FOLLOWER_RANGE_STEPS)[number]);
  if (index >= 0) {
    return FOLLOWER_RANGE_STEP_LABELS[index];
  }
  if (value >= 1000000) {
    const millions = value / 1000000;
    return Number.isInteger(millions) ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return Number.isInteger(thousands) ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  }
  return `${value}`;
}

export function formatFollowerRangeLabel(range: FollowerRangeValue): string {
  return `${formatFollowerStepLabel(range.min)} – ${formatFollowerStepLabel(range.max)}`;
}
